// Refactored ResultsGeotiffLegend with full functionality preserved
import { MapControl, withLeaflet } from "react-leaflet";
import * as L from "leaflet";
import * as plotty from "leaflet-geotiff-2/node_modules/plotty";
import chroma from "chroma-js";
import { connect } from "react-redux";
import { results as resultsColors } from "../../../../common/VisualizationPresets/colorPalettes";
import { withTranslation } from "react-i18next";
import "leaflet.browser.print/dist/leaflet.browser.print";
import "./ResultsGeotiffLegend.scss";

class ResultsGeotiffLegend extends MapControl {
  createLeafletElement(props) { }

  componentDidMount() {
    this.updateLegend();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.resultsJsonOrigin) return;
    this.updateLegend(prevProps);
  }

  componentWillUnmount() {
    // Optional cleanup
  }

  updateLegend(prevProps = {}) {
    if (!this.activeLegends) this.activeLegends = new Set();
    const {
      resultsJsonOrigin,
      currentViewIndex,
      currentActiveResultsView,
      legendVisibilityChanged,
      jsonOriginPartSelected,
      inResultsMapView,
      jsonOriginLayerIndex,
      inLayerAppearance,
      currentCaseId,
      leaflet,
      t,
    } = this.props;

    const layers = resultsJsonOrigin?.pages[currentViewIndex]?.lyrs || [];
    const map = leaflet.map;

    const removeLegend = (layerName) => {
      //const elements = document.getElementsByClassName(layerName);
      const elements = document.querySelectorAll(
        `.${layerName.replace(/\s+/g, "")}.res-geo-legend`
      );

      if (elements[0]) {
        elements[0].remove();
        this.activeLegends.delete(layerName.replace(/\s+/g, ""));
      }
    };

    const createLegend = (layer) => {
      if (layer.type === "wind_dir") return;
      if (this.activeLegends.has(layer.name.replace(/\s+/g, ""))) return; // prevent duplicates
      const legend = L.control({ position: "topright" });
      legend.onAdd = () =>
        this.buildLegendElement(layer, t, currentActiveResultsView);
      legend.addTo(map);
      this.activeLegends.add(layer.name.replace(/\s+/g, ""));
    };

    const shouldRedraw =
      prevProps.currentCaseId !== currentCaseId ||
      resultsJsonOrigin !== prevProps.resultsJsonOrigin ||
      legendVisibilityChanged !== prevProps.legendVisibilityChanged ||
      jsonOriginPartSelected?.name !== prevProps.jsonOriginPartSelected?.name ||
      jsonOriginPartSelected?.plot_color !==
      prevProps.jsonOriginPartSelected?.plot_color ||
      jsonOriginPartSelected?.plot_min !==
      prevProps.jsonOriginPartSelected?.plot_min ||
      jsonOriginPartSelected?.plot_max !==
      prevProps.jsonOriginPartSelected?.plot_max ||
      jsonOriginPartSelected?.plot_preset_cols !==
      prevProps.jsonOriginPartSelected?.plot_preset_cols ||
      (inLayerAppearance === prevProps.inLayerAppearance &&
        (jsonOriginPartSelected?.plot_min !==
          prevProps.jsonOriginPartSelected?.plot_min ||
          jsonOriginPartSelected?.plot_max !==
          prevProps.jsonOriginPartSelected?.plot_max)) ||
      (currentActiveResultsView !== prevProps.currentActiveResultsView &&
        currentActiveResultsView !== "+" &&
        currentActiveResultsView !== "Report") ||
      (!prevProps.inResultsMapView && inResultsMapView);

    if (shouldRedraw) {
      const allPrevLayers =
        prevProps.resultsJsonOrigin?.pages[prevProps.currentViewIndex]?.lyrs ||
        [];
      allPrevLayers.forEach((layer) =>
        removeLegend(layer.name.replace(/\s+/g, ""))
      );
    }

    if (
      layers.length <
      (prevProps.resultsJsonOrigin?.pages[currentViewIndex]?.lyrs?.length || 0)
    ) {
      const removed = this.getDifference(
        prevProps.resultsJsonOrigin.pages[currentViewIndex].lyrs,
        layers
      );
      removed.forEach((layer) => removeLegend(layer.name.replace(/\s+/g, "")));
    }

    layers.forEach((layer) => {
      const exists = document.querySelectorAll(
        `.${layer.name.replace(/\s+/g, "")}.res-geo-legend`
      )[0];

      const shouldAdd =
        layer.checked &&
        !exists &&
        layer.type !== "wind_dir" &&
        layer.type !== "isolines";
      const shouldRemove = !layer.checked && exists;

      if (shouldAdd) createLegend(layer);
      else if (shouldRemove) removeLegend(layer.name);
    });

    const selectedLayer = layers[jsonOriginLayerIndex];
    if (
      selectedLayer?.checked &&
      selectedLayer?.checked !==
      prevProps.resultsJsonOrigin?.pages[currentViewIndex]?.lyrs?.[
        jsonOriginLayerIndex
      ]?.checked
    ) {
      createLegend(selectedLayer);

      L.LegendControl = L.Control.extend({
        onAdd: function (mymap) {
          const source = document.getElementsByClassName(
            "leaflet-top leaflet-right"
          )[0];
          const container = L.DomUtil.create("div", "print-legend-container");
          const gridInner = L.DomUtil.create("div", "print-legend-grid");
          const needsTwoColumns = source?.children?.length > 4;
          if (needsTwoColumns) {
            const numItems = source.children.length;
            const rowCount = Math.ceil(numItems / 2);
            gridInner.style.setProperty("display", "grid", "important");
            gridInner.style.setProperty("grid-template-columns", "1fr 1fr", "important");
            gridInner.style.setProperty("grid-template-rows", `repeat(${rowCount}, max-content)`, "important");
            gridInner.style.setProperty("column-gap", "8px", "important");
            gridInner.style.setProperty("row-gap", "8px", "important");
            gridInner.style.setProperty("align-items", "start", "important");
          }
          if (source?.children?.length) {
            for (let i = 0; i < source.children.length; i++) {
              const wrapper = L.DomUtil.create("div", "legend-wrapper");
              if (needsTwoColumns) {
                const col = Math.floor(i / 3) + 1;
                const row = (i % 3) + 1;
                wrapper.style.setProperty("grid-column", String(col), "important");
                wrapper.style.setProperty("grid-row", String(row), "important");
                wrapper.style.setProperty("align-self", "start", "important");
                wrapper.style.setProperty("justify-self", "start", "important");
              }
              wrapper.appendChild(source.children[i].cloneNode(true));
              gridInner.appendChild(wrapper);
            }
          }
          container.appendChild(gridInner);
          return container;
        },
      });

      L.legendControl = (options) => new L.LegendControl(options);

      this.props.staticDriverMapRef?.current?.leafletElement?.on(
        "browser-print-start",
        (e) => {
          L.legendControl({ position: "bottomright" }).addTo(e.printMap);
        }
      );
    }
  }

  buildLegendElement(layer, t, viewClass) {

    if (layer.type === "wind_dir") return;
    const div = L.DomUtil.create(
      "div",
      `${layer.type === "wind_stream" ? layer.type : ""
      } ${viewClass} ${layer.name.replace(
        /\s+/g,
        ""
      )} res-geo-info res-geo-legend`
    );
    const plotColor = layer.plot_color;

    let colorScale =
      layer.type === "wind_stream" || !plotty.colorscales[plotColor]?.colors
        ? chroma.brewer[plotColor] || resultsColors[plotColor]
        : plotty.colorscales[plotColor].colors;

    if (!colorScale || colorScale.length === 2)
      colorScale = resultsColors[plotColor];
    let breaks = colorScale.length;
    if (!breaks || breaks === 2) breaks = 9;
    if (layer.plot_preset_names) breaks -= 1;

    let plotMin = Number(layer.plot_min);
    let plotMax = Number(layer.plot_max);

    let fixedDigits = plotMin === 0 && plotMax <= 1 ? 2 : 1;
    let interval = Number(((plotMax - plotMin) / breaks).toFixed(fixedDigits));

    let labels = [
      `<label style="font-weight:600; font-family:'Segoe UI'">${layer.set_unit} ${t(
        layer.name
      )}</label>`,
    ];

    for (let i = 0; i < breaks; i++) {
      let value = (plotMin + interval * i).toFixed(fixedDigits);
      if (value.endsWith("0")) value = parseFloat(value).toFixed(1);
      if (value.endsWith("0")) value = parseFloat(value).toFixed(0);

      if (layer.plot_preset_names) value = t(layer.plot_preset_names[i]);
      if (!layer.plot_preset_names && i === breaks - 1) value = plotMax;

      if (!layer.set_sl || (layer.set_sl && i % 2 === 0)) {
        labels.push(`<i style="background:${colorScale[i]}"></i> ${value}`);
      }
    }

    div.innerHTML = labels.join("<br>");
    return div;
  }

  getDifference(array1, array2) {
    return array1.filter((obj1) => !array2.some((obj2) => obj1.id === obj2.id));
  }
}

const mapStateToProps = (state) => ({
  resultsJsonOrigin: state.results?.resultsJsonOrigin,
  currentViewIndex: state.results?.currentViewIndex,
  currentActiveResultsView: state.results?.currentActiveResultsView,
  legendVisibilityChanged: state.results?.legendVisibilityChanged,
  jsonOriginPartSelected: state.results?.jsonOriginPartSelected,
  nrOfAutoPages: state.results?.nrOfAutoPages,
  jsonOriginLayerIndex: state.results?.jsonOriginLayerIndex,
  inLayerAppearance: state.results?.inLayerAppearance,
  currentCaseId: state.projects?.currentCase?.id,
  inResultsMapView: state.results?.inResultsMapView,
  staticDriverMapRef: state.results?.staticDriverMapRef,
});

export default withLeaflet(
  connect(mapStateToProps)(withTranslation()(ResultsGeotiffLegend))
);
